import { useCalculationStore } from '@/stores/calculation'
import { storeToRefs } from 'pinia'
import type { Calculation } from '@/stores/type/calculation.type'
import type { CalculationFormMixinProps } from '@/components/calculation-form/type/type'
import { computed, ref, watch } from 'vue'
import { useDebounceFn } from '@vueuse/core'
import { DEFAULT_DEBOUNCE_DELAY } from '@/utils/debounce'

export function useCalculationForm(props: CalculationFormMixinProps) {
  const calculationStore = useCalculationStore()
  const { currentItem } = storeToRefs(calculationStore)

  const isValid = ref(false)
  const formRef = ref()

  const hasHelpText = (field: string) => {
    return !!calculationStore.fields?.[field]?.help_text
  }

  const getCurrentNumberField = (fieldName: string) => {
    const entry = Object.entries(currentItem.value ?? {}).find(([key]) => key === fieldName)
    const value = entry?.[1]
    return typeof value === 'number' ? value : null
  }

  function lowerValidator(value: number, key: string) {
    const upperKey = key.replace('lower', 'upper')
    const upperValue = getCurrentNumberField(upperKey)
    if (upperValue === null) return true
    return value > upperValue ? 'Invalid' : true
  }

  function upperValidator(value: number, key: string) {
    const lowerKey = key.replace('upper', 'lower')
    const lowerValue = getCurrentNumberField(lowerKey)
    if (lowerValue === null) return true
    return value < lowerValue ? 'Invalid' : true
  }

  watch(isValid, (newValue) => {
    props.updateTabCompletion(props.tab, newValue)
  })

  const isReadOnly = computed(() => {
    return !calculationStore.canChangeCalculation || props.readonly
  })

  const debouncedSave = useDebounceFn(() => {
    if (currentItem.value) calculationStore.update(currentItem.value)
  }, DEFAULT_DEBOUNCE_DELAY)

  const onFieldUpdate = (updatedItem: Calculation) => {
    currentItem.value = updatedItem
    debouncedSave()
  }

  return {
    ...props,
    calculationStore,
    currentItem,
    isValid,
    isReadOnly,
    formRef,
    hasHelpText,
    lowerValidator,
    upperValidator,
    onFieldUpdate
  }
}
